import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle, Warning, Certificate } from "@phosphor-icons/react";

export default function CertificateVerify() {
  const { token } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/setti/certificates/verify/${token}`);
        setResult(data);
      } catch (e) {
        setResult({ valid: false, message: "Unable to verify this certificate." });
      } finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) {
    return <div className="max-w-xl mx-auto px-6 py-20 text-center text-[#5C564D]">Verifying…</div>;
  }

  const valid = result?.valid;
  return (
    <div className="max-w-xl mx-auto px-6 py-16" data-testid="cert-verify">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D95A41]">SETTI — Certificate Verification</div>
      <h1 className="font-heading text-4xl mt-2">
        {valid ? "Certificate verified" : "Not valid"}
      </h1>

      <div className={`mt-6 rounded-2xl border-2 p-6 ${valid ? "bg-[#E6F0E8] border-[#2C4A3B]" : "bg-[#FBE3DC] border-[#D95A41]"}`}>
        <div className="flex items-start gap-3">
          {valid ? (
            <CheckCircle size={28} weight="fill" className="text-[#2C4A3B] flex-shrink-0" />
          ) : (
            <Warning size={28} weight="fill" className="text-[#D95A41] flex-shrink-0" />
          )}
          <div className="flex-1">
            {valid ? (
              <>
                <div className="text-xs text-[#5C564D]">Certificate No.</div>
                <div className="font-mono text-sm font-bold text-[#1C1B1A]" data-testid="cert-no">{result.certificate_no}</div>

                <div className="mt-4 text-xs text-[#5C564D]">Awarded to</div>
                <div className="font-heading text-2xl text-[#1C1B1A]" data-testid="cert-name">{result.candidate_name}</div>

                <div className="mt-4 text-xs text-[#5C564D]">Course</div>
                <div className="text-base font-semibold text-[#2C4A3B]" data-testid="cert-course">{result.course_title}</div>
                <div className="text-xs text-[#5C564D]">
                  {result.course_duration && <>{result.course_duration} • </>}
                  {result.level}
                  {result.final_score_percent != null && <> • Final score {Math.round(result.final_score_percent)}%</>}
                </div>

                <div className="mt-4 text-xs text-[#5C564D]">Issued on</div>
                <div className="text-sm text-[#1C1B1A]">{(result.issued_at || "").slice(0, 10)}</div>

                <div className="mt-6 pt-4 border-t border-[#2C4A3B]/20">
                  <div className="text-xs text-[#5C564D]">Issuer</div>
                  <div className="font-bold text-[#2C4A3B]" data-testid="cert-issuer">
                    <Certificate size={14} className="inline mr-1" weight="fill" />
                    {result.issuer}
                  </div>
                  <div className="text-xs text-[#5C564D]">{result.issuer_parent}</div>
                </div>
              </>
            ) : (
              <div className="text-[#D95A41]">
                {result?.message || "This certificate could not be verified. It may have been revoked, or the link is incorrect."}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 text-xs text-[#5C564D]">
        This verification page is public and read-only. SETTI certificates are issued only after full assessment and assignment completion.
      </div>
    </div>
  );
}
